import { getFormattedPollResponse } from '@/guides/helpers/common.util.js';
import { FilterType } from '@/types/analytics/analytics.types';
import { pluralize } from '@/utils/formatters';
import moment, { DATE_FORMAT } from '@/utils/moment-wrapper';
import capitalize from 'lodash/capitalize';
import escape from 'lodash/escape';
import has from 'lodash/has';
import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import last from 'lodash/last';
import map from 'lodash/map';
import reject from 'lodash/reject';
import { getI18nizedDescription } from './i18n-summary';
import { isoDatesToTimestamps } from './iso-dates-to-timestamps';
import { FRUSTRATION_TYPES } from '@/session-replay/helpers/common.util.js';

const opLookupMap = {
    'used': 'used',
    'lastused': 'last used',
    'not used': 'not used',
    'notused': 'not used',
    'viewed': 'viewed',
    'lastviewed': 'last viewed',
    'notviewed': 'not viewed',
    'completed': 'completed',
    'notstarted': 'not started',
    'incomplete': 'not completed',
    '==': 'is equal to',
    '!=': 'is not equal to',
    '>=': 'is greater or equal to',
    '<=': 'is lesser or equal to',
    '>': 'is greater than',
    '<': 'is less than',
    'between': 'between',
    'contains': 'contains',
    '!contains': 'not contains',
    'empty': 'is empty',
    '!empty': 'is not empty',
    'listContains': 'contained within list',
    '!listContains': 'not contained within list',
    'responded': 'responded',
    '!responded': 'not responded',
    'withinLast': 'within last',
    '!withinLast': 'is not within last',
    'withinNext': 'is within next',
    '!withinNext': 'is not within next',
    'startsWith': 'starts with',
    '!startsWith': 'does not start with',
    'entered': 'entered',
    'notentered': 'not entered',
    'delivered': 'delivered',
    'notdelivered': 'not delivered',
    'opened': 'opened',
    'notopened': 'not opened',
    'clicked': 'clicked',
    'notclicked': 'not clicked',
};

function transformValue (schemaField, rule) {
    if (rule.operator === 'empty' || rule.operator === '!empty') {
        return '';
    }

    if (
        schemaField.Type === 'time' ||
        schemaField.Type === 'date' ||
        schemaField.schema === 'time' ||
        schemaField.schema === 'date'
    ) {
        return '';
    }
    if (/!?listContains/.test(rule.operator)) {
        return `"${rule.value}"`;
    }

    return rule.value == null ? '' : rule.value;
}

function createEventPropertyDescriptor (rule) {
    const { eventPropertyName, eventPropertyField, eventPropertyOperator, eventPropertyValue, filterType } = rule;
    const displayName = eventPropertyName || eventPropertyField;
    let descriptorStr = '';
    if (filterType === FilterType.Location) {
        if (eventPropertyField === 'region') {
            descriptorStr = 'with region';
        } else if (eventPropertyField === 'country') {
            descriptorStr = 'with country';
        } else {
            descriptorStr = `with location property ${displayName}`;
        }
    } else if (filterType === FilterType.EventTopLevel && eventPropertyField === 'server') {
        descriptorStr = 'with server name';
    } else if (filterType === FilterType.PageParameter && eventPropertyField === 'parameters.parameter') {
        descriptorStr = 'with page parameter';
    } else if (filterType === FilterType.Utm) {
        descriptorStr = `with session property ${displayName}`;
    } else {
        descriptorStr = `with event property ${displayName}`;
    }

    if (isBoolean(eventPropertyValue)) {
        return `${descriptorStr} equal to ${eventPropertyValue}`;
    }

    const operatorDescriptor = `${opLookupMap[eventPropertyOperator]}`;

    if (isUndefined(eventPropertyValue)) {
        return `${descriptorStr} ${operatorDescriptor}`;
    }

    return `${descriptorStr} ${operatorDescriptor} ${eventPropertyValue}`;
}

function createAtLeastOrAtmostDescriptor (rule, usedKey) {
    const labelLookup = {
        atleast: 'at least',
        atmost: 'at most',
    };
    const numOfTimes = pluralize('time', rule.value);

    return `${labelLookup[rule[usedKey]]} ${rule.value} ${numOfTimes}`;
}

function createTimeDescriptor (rule, timeKey = 'time') {
    const timeLookup = {
        '>=': 'since',
        '<=': 'before',
        'since': 'since',
        'before': 'before',
        'trailng': 'within trailing',
        'withinLast': 'within last',
        '!withinLast': 'not within last',
        'withinNext': 'within next',
        '!withinNext': 'not within next',
    };

    let timeOrOperator = rule[timeKey];
    if (!timeOrOperator && rule.schema === 'time' && rule.operator) {
        timeOrOperator = `${rule.operator}`;
    }

    switch (timeOrOperator) {
        case 'since':
        case 'before':
        case '>=':
        case '<=': {
            const dateValue = moment(rule.first || rule.value, { ignoreTimeZone: true }).format(DATE_FORMAT.short);

            return `${timeLookup[timeOrOperator]} ${dateValue}`;
        }
        case 'between': {
            const firstDate = moment(rule.first, { ignoreTimeZone: true }).format(DATE_FORMAT.short);
            const lastDate = moment(rule.last, { ignoreTimeZone: true }).format(DATE_FORMAT.short);

            return `between ${firstDate} and ${lastDate}`;
        }
        case 'trailing':
        case 'withinLast':
        case '!withinLast':
        case 'withinNext':
        case '!withinNext': {
            return `${timeLookup[timeOrOperator]} ${rule.count} ${rule.granularity}`;
        }
        case '==':
        case '!=':
            return `${opLookupMap[rule.operator]}`;
        case 'ever':
            return 'ever';
    }

    if (timeOrOperator) {
        return `${opLookupMap[rule.operator]}`;
    }

    return '';
}

function getSchemaFieldForRule (rule, schema) {
    const idx = rule.kind === 'visitor' ? 0 : 1;
    let { field } = rule;

    if (field && /\./.test(field)) {
        field = last(field.split('.'));
    }
    if (schema) {
        let tmp = schema[idx] || {};
        tmp = tmp[rule.group] || {};

        return tmp[field];
    }
}

function getFieldDisplayName (rule, field) {
    if (field && field.DisplayName) {
        return field.DisplayName;
    }
    if (rule.name) {
        return rule.name;
    }

    return rule.field ? last(rule.field.split('.')) : rule.field;
}

function findTag (tags, criteria) {
    if (tags) {
        return tags.find((item) => Object.keys(criteria).every((key) => item[key] === criteria[key]));
    }
}

function createDescription (rule, resources) {
    const { schema, tags, segments, apps } = resources;

    const i18nizedDescription = getI18nizedDescription(rule, resources);
    if (i18nizedDescription) {
        return i18nizedDescription;
    }

    const visitorTagLabelMap = {
        page: 'Page',
        feature: 'Feature',
        trackType: 'Track Event',
    };

    if (['page', 'feature', 'trackType'].includes(rule.condition)) {
        const tag = findTag(tags, { id: rule.id });
        const op = rule.seen ? 'used' : 'not used';
        const label = visitorTagLabelMap[rule.condition];

        return {
            text: `${label} ${tag ? tag.name : 'Unknown'} is ${op}`,
            since: rule.since,
        };
    }

    if (['page', 'feature', 'trackType'].includes(rule.type)) {
        const tag = findTag(tags, { id: rule.pageId || rule.featureId || rule.trackTypeId });
        const tagName = tag ? tag.name : 'Unknown';
        const tagSentenceInfo = `${visitorTagLabelMap[rule.type]} ${tagName}`;
        let operatorDescription;
        const factStr = rule.type === 'page' ? 'seen' : 'used';
        const sourceTypeBasedEventsMap = {
            page: 'Page views',
            feature: 'Feature clicks',
            trackType: 'Track Events',
        };

        switch (rule.fact) {
            case 'used':
                operatorDescription = factStr;
                break;
            case 'notused':
                operatorDescription = `not ${factStr}`;
                break;
            case 'lastused':
                operatorDescription = `last ${factStr}`;
                break;
            case 'eventCount': {
                const factStr = `number of ${sourceTypeBasedEventsMap[rule.type]}`;
                const opStr = opLookupMap[rule.operator];
                const timeStr = createTimeDescriptor(rule, 'time');

                operatorDescription = `${factStr} ${opStr} ${rule.value} ${timeStr}`;
                break;
            }
            case 'deadClicks': {
                const opStr = opLookupMap[rule.operator];
                const timeStr = createTimeDescriptor(rule, 'time');

                operatorDescription = `number of dead clicks ${opStr} ${rule.value} ${timeStr}`;
                break;
            }
            case 'errorClicks': {
                const opStr = opLookupMap[rule.operator];
                const timeStr = createTimeDescriptor(rule, 'time');

                operatorDescription = `number of error clicks ${opStr} ${rule.value} ${timeStr}`;
                break;
            }
            case 'rageClicks': {
                const opStr = opLookupMap[rule.operator];
                const timeStr = createTimeDescriptor(rule, 'time');

                operatorDescription = `number of rage clicks ${opStr} ${rule.value} ${timeStr}`;
                break;
            }
            case 'uTurns': {
                const opStr = opLookupMap[rule.operator];
                const timeStr = createTimeDescriptor(rule, 'time');

                operatorDescription = `number of u-turns ${opStr} ${rule.value} ${timeStr}`;
                break;
            }
            case 'eventTime': {
                const numOfMinutes = pluralize('minute', rule.value);
                const opStr = opLookupMap[rule.operator];
                const timeStr = createTimeDescriptor(rule, 'time');

                operatorDescription = `time on ${rule.name} ${opStr} ${rule.value} ${numOfMinutes} ${timeStr}`;
                break;
            }
            case 'numVisitors':
            case 'numAccounts':
            case 'daysActive': {
                const factStrLookup = {
                    daysActive: 'number of days active',
                    numAccounts: 'number of Accounts',
                    numVisitors: 'number of Visitors',
                };

                const factStr = factStrLookup[rule.fact];
                const opStr = opLookupMap[rule.operator];
                const timeStr = createTimeDescriptor(rule, 'time');

                operatorDescription = `${factStr} ${opStr} ${rule.value} ${timeStr}`;
                break;
            }
        }

        if (rule.schema === 'counter') {
            switch (rule.used) {
                case 'atleast':
                case 'atmost':
                    operatorDescription += ` ${createAtLeastOrAtmostDescriptor(rule, 'used')}`;
                    break;
                case 'ever':
                case 'before':
                case 'since':
                case 'withinLast':
                case '!withinLast':
                case 'between':
                    operatorDescription += ` ${createTimeDescriptor(rule, 'used')}`;
                    break;
            }
        }

        if (rule.eventPropertyName || (rule.eventPropertyField && has(rule, 'eventPropertyValue'))) {
            operatorDescription += ` ${createEventPropertyDescriptor(rule)}`;
        }

        return {
            text: `${tagSentenceInfo} ${operatorDescription}`.trim(),
        };
    }
    if (FRUSTRATION_TYPES.includes(rule.fact)) {
        let operatorDescription = '';
        switch (rule.fact) {
            case 'deadClicks': {
                const opStr = opLookupMap[rule.operator];
                const timeStr = createTimeDescriptor(rule, 'time');

                operatorDescription = `Number of dead clicks ${opStr} ${rule.value} ${timeStr}`;
                break;
            }
            case 'errorClicks': {
                const opStr = opLookupMap[rule.operator];
                const timeStr = createTimeDescriptor(rule, 'time');

                operatorDescription = `Number of error clicks ${opStr} ${rule.value} ${timeStr}`;
                break;
            }
            case 'rageClicks': {
                const opStr = opLookupMap[rule.operator];
                const timeStr = createTimeDescriptor(rule, 'time');

                operatorDescription = `Number of rage clicks ${opStr} ${rule.value} ${timeStr}`;
                break;
            }
            case 'uTurns': {
                const opStr = opLookupMap[rule.operator];
                const timeStr = createTimeDescriptor(rule, 'time');

                operatorDescription = `Number of u-turns ${opStr} ${rule.value} ${timeStr}`;
                break;
            }
        }
        if (rule.schema === 'counter') {
            switch (rule.used) {
                case 'atleast':
                case 'atmost':
                    operatorDescription += ` ${createAtLeastOrAtmostDescriptor(rule, 'used')}`;
                    break;
                case 'ever':
                case 'before':
                case 'since':
                case 'withinLast':
                case '!withinLast':
                case 'between':
                    operatorDescription += ` ${createTimeDescriptor(rule, 'used')}`;
                    break;
            }
        }

        if (rule.eventPropertyName || (rule.eventPropertyField && has(rule, 'eventPropertyValue'))) {
            operatorDescription += ` ${createEventPropertyDescriptor(rule)}`;
        }

        return {
            text: `${operatorDescription}`.trim(),
        };
    }

    if (rule.type === 'poll') {
        const guide = findTag(tags, { id: rule.guideId });
        const poll = findTag(guide && guide.polls, { id: rule.pollId }) || { question: 'Unknown Poll' };

        if (poll) {
            const pollSentenceInfo = `Response to "${poll.question}"`;
            let operatorDescription = `${opLookupMap[rule.operator] || rule.operator}`;

            if (rule.operator.indexOf('responded') === -1) {
                operatorDescription += ` ${getFormattedPollResponse(rule.value, poll)}`;
            }

            operatorDescription += ` ${createTimeDescriptor(rule, 'time')}`;

            return {
                text: `${pollSentenceInfo} ${operatorDescription}`.trim(),
            };
        }

        return { text: '' };
    }

    if (rule.type === 'application') {
        const app = findTag(apps, { id: rule.appId });
        const appName = app?.displayName || app?.name || 'Unknown';
        const applicationSentenceInfo = `Application ${appName} ${opLookupMap[rule.fact]}`;
        const operatorDescription = createTimeDescriptor(rule, 'used');

        return {
            id: rule.id,
            appId: rule.appId,
            text: `${applicationSentenceInfo} ${operatorDescription}`.trim(),
        };
    }

    if (rule.type === 'guide' || rule.type === 'journey') {
        const tag =
            rule.type === 'guide'
                ? findTag(tags, { id: rule.guideId })
                : findTag(tags, { staticSegmentId: rule.staticSegmentId });
        const tagSentenceInfo = `${rule.type === 'guide' ? 'Guide' : 'Journey'} ${tag ? tag.name : 'Unknown'} ${
            opLookupMap[rule.fact]
        }`;
        let operatorDescription = rule.time;

        switch (rule.time) {
            case 'atmost':
            case 'atleast': {
                operatorDescription = createAtLeastOrAtmostDescriptor(rule, 'time');
                break;
            }
            case 'withinLast':
            case '!withinLast':
            case '>=':
            case '<=':
            case 'between':
                operatorDescription = createTimeDescriptor(rule, 'time');
                break;
        }

        return { text: `${tagSentenceInfo} ${operatorDescription}`.trim() };
    }

    if (rule.type === 'guideElement') {
        const guide = findTag(tags, { id: rule.guideId });

        let op;
        switch (rule.fact) {
            case 'clicked':
                op = 'clicked';
                break;
            case 'notclicked':
                op = 'not clicked';
                break;
            case 'eventCount': {
                const numOfTimes = pluralize('time', rule.value);
                op =
                    `clicked ${opLookupMap[rule.operator]} ` +
                    `${rule.value} ${numOfTimes} ${createTimeDescriptor(rule)}`;
                break;
            }
        }

        switch (rule.clicked) {
            case 'atleast':
            case 'atmost':
                op += ` ${createAtLeastOrAtmostDescriptor(rule, 'clicked')}`;
                break;
            case 'withinLast':
            case 'since': {
                op += ` ${createTimeDescriptor(rule, 'clicked')}`;
                break;
            }
        }

        return {
            text: `Element ${guide ? guide.name : 'Unknown'} Element ${op}`,
        };
    }

    if (rule.type === 'emailElement') {
        const email = findTag(tags, { id: rule.emailId });

        let op;
        switch (rule.fact) {
            case 'clicked':
                op = 'clicked';
                break;
            case 'notclicked':
                op = 'not clicked';
                break;
            case 'eventCount': {
                const numOfTimes = pluralize('time', rule.value);
                op =
                    `clicked ${opLookupMap[rule.operator]} ` +
                    `${rule.value} ${numOfTimes} ${createTimeDescriptor(rule)}`;
                break;
            }
        }

        switch (rule.clicked) {
            case 'atleast':
            case 'atmost':
                op += ` ${createAtLeastOrAtmostDescriptor(rule, 'clicked')}`;
                break;
            case 'withinLast':
            case 'since': {
                op += ` ${createTimeDescriptor(rule, 'clicked')}`;
                break;
            }
        }

        return {
            text: `Element ${email ? email.name : 'Unknown'} Element ${op}`,
        };
    }

    if (rule.type === 'email') {
        const email = findTag(tags, { id: rule.emailId });
        const emailSentenceInfo = `Email ${email ? email.name : 'Unknown'} ${opLookupMap[rule.fact]}`;
        let operatorDescription = rule.time;

        switch (rule.time) {
            case 'withinLast':
            case '!withinLast':
            case '>=':
            case '<=':
            case 'between':
                operatorDescription = createTimeDescriptor(rule, 'time');
                break;
        }

        return {
            text: `${emailSentenceInfo} ${operatorDescription}`.trim(),
        };
    }

    if (rule.type === 'feedbackRequest') {
        return {
            text: `FeedbackRequest Voters of Feedback Request #${rule.feedbackRequestId}`,
        };
    }

    if (rule.type === 'idea') {
        const ideaImportanceNameMap = {
            'positive': 'Must have or Nice to have',
            '1': 'Nice to have',
            '2': 'Must have',
            '-1': 'Not interested',
        };

        let desc = 'Idea ';
        if (rule.hasVoted) {
            if (rule.ideaId === '*') {
                desc += 'Voters of any idea';
            } else {
                desc += `Voters of idea '${rule.ideaId}'`;
            }
            const importance = String(rule.importance);
            if (importance !== 'any' && ideaImportanceNameMap[importance]) {
                desc += ` who voted '${ideaImportanceNameMap[importance]}'`;
            }
        } else if (rule.ideaId === '*') {
            desc += 'Visitors who have not voted on any idea';
        } else {
            desc += `Visitors who did not vote on '${rule.ideaId}'`;
        }
        if (rule.time === 'withinLast' || rule.time === 'between') {
            desc += ` ${createTimeDescriptor(rule)}`;
        }

        return {
            text: desc,
        };
    }

    if (rule.segmentId) {
        const segment = findTag(segments, { id: rule.segmentId });
        const segmentName = segment ? segment.name : 'Unknown Segment';
        const clsString = 'segment';

        if (rule.type === 'segment') {
            return {
                text: `Segment ${opLookupMap[rule.operator]}`,
                clsString,
                entityName: segmentName,
                entityRoute: { name: 'Segments', query: { segment: rule.segmentId } },
                entityDeleted: !segment,
                segmentId: rule.segmentId,
            };
        }

        return { text: escape(segmentName), clsString };
    }

    if (rule.schema === 'aggregation') {
        let text =
            {
                daysActive: 'Number of days active',
                eventCount: 'Number of events',
                eventTime: 'Time on site',
                numAccounts: 'Number of Accounts',
                numVisitors: 'Number of Visitors',
            }[rule.fact] || rule.fact;

        if (rule.pageId) {
            const page = findTag(tags, { id: rule.pageId });
            text += ` on ${page ? `Page ${page.name}` : 'Unknown Page'}`;
        } else if (rule.featureId) {
            const feature = findTag(tags, { id: rule.featureId });
            text += ` on ${feature ? `Feature ${feature.name}` : 'Unknown Feature'}`;
        } else if (rule.trackTypeId) {
            const trackType = findTag(tags, { id: rule.trackTypeId });
            text += ` on ${trackType ? `Track Event ${trackType.name}` : 'Unknown Track Event'}`;
        } else if (rule.id === 'utm') {
            text = rule.name;
        }

        text += ` ${opLookupMap[rule.operator]} ${rule.value}`;
        const time = createTimeDescriptor(rule);
        text += time ? ` ${time}` : '';

        return {
            text,
        };
    }

    if (['appVersion', 'sdkVersion'].includes(rule.id)) {
        return {
            text: `Mobile ${rule.name} ${opLookupMap[rule.operator]} ${rule.value}`,
        };
    }

    const field = getSchemaFieldForRule(rule, schema);
    const isTime = rule.schema === 'time' || schema === 'time';
    const kindStr = capitalize(rule.kind);
    const displayNameStr = getFieldDisplayName(rule, field);
    const timeStr = isTime ? createTimeDescriptor(rule) : `${opLookupMap[rule.operator]}`;
    const parts = reject([kindStr, displayNameStr, timeStr, transformValue(field || rule, rule).toString()], isEmpty);

    return {
        text: parts.join(' '),
        since: rule.since,
    };
}

export function getFilterSummary (rules, resources) {
    rules = isoDatesToTimestamps(rules);

    return map(rules, (rule) => {
        if (rule.or) {
            return {
                or: map(rule.or, (condition) => {
                    return createDescription(condition, resources);
                }),
            };
        }

        if (rule.condition !== 'until') {
            return createDescription(rule, resources);
        }

        return rule;
    });
}
